/**
 * FlexFreight Admin Console access model: staff roles and the capabilities each one grants.
 * Single source of truth for the API (`requireCap`) and the console UI (hiding what a role can't use).
 * The API enforces these on every /v1/console route; UI hiding is convenience only.
 */
export const CONSOLE_ROLES = ["owner", "admin", "ops", "sales", "marketing", "read_only"] as const;
export type ConsoleRole = (typeof CONSOLE_ROLES)[number];

export const CONSOLE_CAPABILITIES = [
  "dashboard:read",
  "quotes:read",
  "quotes:create",
  "quotes:override",
  "quotes:book",
  "pricing:cost_read",
  /** Customers > one account > Markup: set the customer's default markup (their sell price over carrier cost). */
  "pricing:write",
  "customers:read",
  "customers:write",
  "customers:impersonate",
  "customers:act_as",
  "leads:read",
  "leads:write",
  "shipments:read",
  "shipments:write",
  /** Freight > Dispatch board and AmFast tenders: claim/snooze follow-ups, record ops-entered carrier status and documents. */
  "dispatch:write",
  /** Freight > Shipment import: load a customer's shipment history from an EagleLoad CSV (writes shipments). */
  "shipments:import",
  "claims:read",
  "claims:write",
  "credit:read",
  "credit:decide",
  "billing:read",
  "billing:write",
  "carriers:read",
  "carriers:manage",
  "carriers:go_live",
  "tickets:read",
  "tickets:write",
  "kb:read",
  "kb:write",
  "email:read",
  "email:send",
  "marketing:read",
  "marketing:write",
  "marketing:approve",
  "reports:read",
  "ai:use",
  "integrations:manage",
  "staff:manage",
  "settings:read",
  "settings:manage",
  "audit:read",
] as const;
export type ConsoleCapability = (typeof CONSOLE_CAPABILITIES)[number];

const ALL: readonly ConsoleCapability[] = CONSOLE_CAPABILITIES;
const READ: readonly ConsoleCapability[] = [
  "dashboard:read",
  "quotes:read",
  "customers:read",
  "leads:read",
  "shipments:read",
  "claims:read",
  "carriers:read",
  "tickets:read",
  "kb:read",
  "reports:read",
];

export const CONSOLE_ROLE_CAPABILITIES: Readonly<Record<ConsoleRole, ReadonlySet<ConsoleCapability>>> = {
  owner: new Set(ALL),
  // Admin has every capability; only an owner can grant or remove the owner role (enforced in the staff API).
  admin: new Set(ALL),
  ops: new Set<ConsoleCapability>([
    ...READ,
    "quotes:create",
    "quotes:override",
    "quotes:book",
    "pricing:cost_read",
    "pricing:write",
    "customers:write",
    "customers:impersonate",
    "shipments:write",
    "dispatch:write",
    "claims:write",
    "credit:read",
    "billing:read",
    "tickets:write",
    "kb:write",
    "email:read",
    "email:send",
    "ai:use",
    "settings:read",
  ]),
  sales: new Set<ConsoleCapability>([
    ...READ,
    "quotes:create",
    "quotes:book",
    "pricing:cost_read",
    "customers:write",
    "customers:impersonate",
    "leads:write",
    "tickets:write",
    "email:read",
    "email:send",
    "marketing:read",
    "ai:use",
  ]),
  marketing: new Set<ConsoleCapability>([
    "dashboard:read",
    "customers:read",
    "leads:read",
    "leads:write",
    "kb:read",
    "kb:write",
    "email:read",
    "marketing:read",
    "marketing:write",
    "reports:read",
    "ai:use",
  ]),
  read_only: new Set(READ),
};

/** Roles that must have two-factor sign-in turned on before the console lets them in. */
export const CONSOLE_MFA_REQUIRED: ReadonlySet<ConsoleRole> = new Set<ConsoleRole>(["owner", "admin"]);

/**
 * Effective console role. An explicit console role wins; otherwise the legacy `staff_role` maps over
 * (admin -> admin, ops -> ops). Customers (neither set) have no console role.
 */
export function consoleRoleOf(consoleRole: ConsoleRole | null | undefined, staffRole: "ops" | "admin" | null | undefined): ConsoleRole | null {
  if (consoleRole) return consoleRole;
  if (staffRole === "admin") return "admin";
  if (staffRole === "ops") return "ops";
  return null;
}

export function consoleCan(role: ConsoleRole | null, cap: ConsoleCapability): boolean {
  return role !== null && CONSOLE_ROLE_CAPABILITIES[role].has(cap);
}

export function consoleCapabilities(role: ConsoleRole | null): ConsoleCapability[] {
  return role === null ? [] : CONSOLE_CAPABILITIES.filter((c) => CONSOLE_ROLE_CAPABILITIES[role].has(c));
}

/**
 * Staff who may hand out owner-level access to a customer company: grant the customer `owner` role (Add user, role
 * changes) or create a copyable sign-in link for someone's account. Console owner or admin with an authenticator or
 * text codes on (`strongFactor`). Mirrors the portal's `canTouchOwners`: only owners, or FlexFreight admins, touch
 * owner access. Other customers:write roles may give `admin` or below. Add customer may still create the first owner
 * of a brand-new company.
 */
export function consoleCanGrantCustomerOwner(role: ConsoleRole | null, strongFactor: boolean): boolean {
  return role !== null && CONSOLE_MFA_REQUIRED.has(role) && strongFactor;
}

/** Staff who may change where customers open the portal (Admin Center > Company): console owner with a strong factor. */
export function consoleCanSetPortalAddress(role: ConsoleRole | null, strongFactor: boolean): boolean {
  return role === "owner" && strongFactor;
}
